import { compact, groupBy, intersection, isEmpty, property } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useFormContext } from '../../components/forms/forms';
import { useAccounts } from '../../hooks/data/auth';
import { ReportsSelectionSection } from './ReportsSelectionSection';

const SPLIT_INTO_GROUPS = items => {
  const { true: partnerAccounts = [], false: nonPartnerAccounts = [] } =
    groupBy(items, acc => acc.partner);

  return compact([
    !isEmpty(partnerAccounts) && {
      key: 'partner',
      labelId: 'reports.accountSelection.partnerAccess',
      variant: 'partner',
      items: partnerAccounts,
    },
    !isEmpty(nonPartnerAccounts) && {
      key: 'non-partner',
      labelId: 'reports.accountSelection.accounts',
      variant: 'nonPartner',
      items: nonPartnerAccounts,
    },
  ]);
};

const COLUMNS = [
  {
    key: 'number',
    labelId: 'reports.accountSelection.accountNumber',
    span: 8,
    render: acc => acc.number,
  },
  {
    key: 'name',
    labelId: 'reports.accountSelection.customerName',
    span: 16,
    render: acc => acc.name,
  },
];

export function ReportsAccountSelection() {
  const { formatMessage } = useIntl();
  const { customerAccounts = [], error, loading } = useAccounts();
  const { values, formInstance, forceUpdate } = useFormContext();
  const { selectedReports = [], selectedAccounts = [] } = values || {};

  // Get the valid accounts for the selected reports
  const validAccounts = useMemo(() => {
    if (!customerAccounts) return [];
    if (isEmpty(selectedReports)) return customerAccounts;

    const reportAccountSets = selectedReports.map(
      report => report.accountNumbers || []
    );
    const validAccountNumbers = intersection(...reportAccountSets);
    return customerAccounts.filter(acc =>
      validAccountNumbers.includes(acc.number)
    );
  }, [customerAccounts, selectedReports]);

  // Sync the selected accounts with the new valid accounts
  useEffect(() => {
    const validAccountNumbers = validAccounts.map(a => a.number);
    const newSelected = selectedAccounts.filter(a =>
      validAccountNumbers.includes(a.number)
    );

    if (newSelected.length !== selectedAccounts.length) {
      formInstance.setFieldsValue({ selectedAccounts: newSelected });
      forceUpdate();
    }
  }, [validAccounts, selectedAccounts, formInstance, forceUpdate]);

  const isItemDisabled = acc =>
    !validAccounts.some(va => va.number === acc.number);

  const getDisabledReason = acc => {
    for (const report of selectedReports) {
      if (!report.accountNumbers?.includes(acc.number)) {
        return formatMessage(
          { id: 'reports.accountSelection.disabledReason' },
          { report: report.text }
        );
      }
    }
    return '';
  };

  return (
    <ReportsSelectionSection
      index={2}
      titleId="reports.chooseAccounts"
      headerId="reports.accountSelection"
      subtitleId="reports.accountSelection.subtitle"
      searchPlaceholderId="reports.accountSearch.placeholder"
      items={customerAccounts}
      loading={loading}
      error={error}
      getSearchSourcesFromItem={acc => [acc.name, acc.number]}
      splitIntoGroups={SPLIT_INTO_GROUPS}
      fieldName="selectedAccounts"
      getKey={property('number')}
      getLabel={property('number')}
      columns={COLUMNS}
      isItemDisabled={isItemDisabled}
      getDisabledReason={getDisabledReason}
    />
  );
}
